import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { AlertTriangle, TrendingDown, LifeBuoy } from 'lucide-react';

// --------- HELPERS ----------
const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

function computeSafeTooltipPosition({ barRect, containerRect, tipW, tipH, prefAbove = true }) {
  const PADDING = 8;
  const OFFSET  = 10;

  // centro da barra relativo ao container
  let x = (barRect.left - containerRect.left) + (barRect.width / 2);

  const yTop    = (barRect.top - containerRect.top) - OFFSET - tipH; // acima
  const yBottom = (barRect.bottom - containerRect.top) + OFFSET;      // abaixo

  let placeAbove = prefAbove;
  let y = placeAbove ? yTop : yBottom;

  // se acima não cabe, tenta abaixo
  if (placeAbove && y < PADDING) {
    placeAbove = false;
    y = yBottom;
  }

  const containerH = containerRect.height;
  // se abaixo não cabe, volta acima
  if (!placeAbove && (y + tipH + PADDING > containerH)) {
    placeAbove = true;
    y = yTop;
  }

  // clamp final
  y = clamp(y, PADDING, Math.max(PADDING, containerH - tipH - PADDING));

  // como usamos translateX(-50%), clamp considerando meia-largura
  const minCenter = PADDING + tipW / 2;
  const maxCenter = containerRect.width - PADDING - tipW / 2;
  x = clamp(x, minCenter, maxCenter);

  return { x, y, placeAbove };
}
// ----------------------------

export default function AccidentChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBarKey, setHoveredBarKey] = useState(null); // `${index}-accidents` | `${index}-fatalities`
  const [tooltipData, setTooltipData] = useState(null);      // {year, value, label}
  const [tooltipPos, setTooltipPos]   = useState(null);      // {x, y, placeAbove}
  const [isMobile, setIsMobile]       = useState(false);

  // Refs
  const scrollRef       = useRef(null);       // wrapper com overflow-x (mobile)
  const innerRefDesktop = useRef(null);       // container RELATIVE (desktop)
  const innerRefMobile  = useRef(null);       // container RELATIVE (mobile)
  const tooltipRef      = useRef(null);       // mede tamanho do tooltip

  const accidentData = [
    { year: 2010, accidents: 948,  fatalities: 251 }, { year: 2011, accidents: 970,  fatalities: 275 },
    { year: 2012, accidents: 923,  fatalities: 224 }, { year: 2013, accidents: 934,  fatalities: 258 },
    { year: 2014, accidents: 906,  fatalities: 247 }, { year: 2015, accidents: 999,  fatalities: 258 },
    { year: 2016, accidents: 901,  fatalities: 180 }, { year: 2017, accidents: 872,  fatalities: 201 },
    { year: 2018, accidents: 832,  fatalities: 262 }, { year: 2019, accidents: 1043, fatalities: 294 },
    { year: 2020, accidents: 804,  fatalities: 243 }, { year: 2021, accidents: 894,  fatalities: 261 },
    { year: 2022, accidents: 888,  fatalities: 244 }, { year: 2023, accidents: 888,  fatalities: 245 },
    { year: 2024, accidents: 848,  fatalities: 217 }
  ];

  const FATALITIES_SCALE = 3; // escala visual x3
  const CHART_HEIGHT = 320;
  const maxValue = Math.max(...accidentData.map(d => Math.max(d.accidents, d.fatalities * FATALITIES_SCALE)));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setAnimatedData(accidentData), 400);
    return () => clearTimeout(t);
  }, []);

  const getBarHeight = (value) => (value / maxValue) * (CHART_HEIGHT - 20);

  // Tooltip seguro (duplo RAF para iOS/Safari)
  const showTooltip = (item, key, index, targetEl) => {
    const container = (isMobile ? innerRefMobile : innerRefDesktop).current;
    if (!container) return;

    const barRect       = targetEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    setHoveredBarKey(`${index}-${key}`);

    const isAcc   = key === 'accidents';
    const value   = isAcc ? item.accidents : item.fatalities;
    const label   = isAcc ? 'Acidentes' : 'Vítimas Fatais';

    // 1) Renderiza tooltip
    setTooltipData({ year: item.year, value, label });

    // 2) Mede no(s) próximo(s) frame(s)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const tip = tooltipRef.current;
        const tipW = tip ? tip.offsetWidth  : 140;
        const tipH = tip ? tip.offsetHeight : 44;

        const { x, y, placeAbove } = computeSafeTooltipPosition({
          barRect,
          containerRect,
          tipW,
          tipH,
          prefAbove: !isMobile // mobile: abaixo; desktop: acima
        });

        setTooltipPos({ x, y, placeAbove });
      });
    });
  };

  const handleEnter = (item, key, index) => (e) => showTooltip(item, key, index, e.currentTarget);
  const handleLeave = () => { setHoveredBarKey(null); setTooltipData(null); setTooltipPos(null); };

  // Pointer Events unificados
  const getBarHandlers = (item, key, index) => ({
    onPointerDown:  (e) => handleEnter(item, key, index)(e),              // tap/click abre
    onPointerEnter: (e) => !isMobile && handleEnter(item, key, index)(e), // hover desktop
    onPointerMove:  (e) => !isMobile && handleEnter(item, key, index)(e),
    onPointerLeave: handleLeave,
  });

  // medidas mobile
  const BAR_W = 14;
  const GAP_W = 6;
  const SLOT_W = BAR_W * 2 + GAP_W + 8;
  const contentWidthMobile = accidentData.length * SLOT_W + 16;
  const touchSafeStyle = { touchAction: 'manipulation', userSelect: 'none' };

  return (
    <div className="space-y-8">
      <Card>
        <CardContent className="p-6">
          {isMobile ? (
            // --- MOBILE: scroll horizontal + container relativo ---
            <div ref={scrollRef} className="overflow-x-auto no-scrollbar" onScroll={handleLeave}>
              <div
                ref={innerRefMobile}
                className="relative flex items-end px-4"
                style={{ height: CHART_HEIGHT, width: contentWidthMobile }}
              >
                {accidentData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end" style={{ width: SLOT_W }}>
                    <div className="flex items-end gap-1">
                      {/* Acidentes */}
                      <div
                        className={`rounded-t bg-blue-500 transition-all duration-300 cursor-pointer ${hoveredBarKey === `${index}-accidents` ? 'opacity-90' : ''}`}
                        style={{
                          ...touchSafeStyle,
                          width: BAR_W,
                          height: `${animatedData[index] ? getBarHeight(item.accidents) : 0}px`,
                          transitionDelay: `${index * 35}ms`,
                          transform: hoveredBarKey === `${index}-accidents` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, 'accidents', index)}
                      />
                      {/* Vítimas (escala x3) */}
                      <div
                        className={`rounded-t bg-red-500 transition-all duration-300 cursor-pointer ${hoveredBarKey === `${index}-fatalities` ? 'opacity-90' : ''}`}
                        style={{
                          ...touchSafeStyle,
                          width: BAR_W,
                          height: `${animatedData[index] ? getBarHeight(item.fatalities * FATALITIES_SCALE) : 0}px`,
                          transitionDelay: `${index * 35 + 20}ms`,
                          transform: hoveredBarKey === `${index}-fatalities` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, 'fatalities', index)}
                      />
                    </div>
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                    style={{
                      left: tooltipPos ? tooltipPos.x : -9999,
                      top: tooltipPos ? tooltipPos.y : -9999,
                      transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                    }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">
                      {tooltipData.label}: {tooltipData.value.toLocaleString('pt-BR')}
                      {tooltipData.label === 'Vítimas Fatais' && <span className="opacity-70"> (x{FATALITIES_SCALE})</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // --- DESKTOP ---
            <div ref={innerRefDesktop} className="h-80 px-4 relative">
              <div className="flex items-end justify-between h-full gap-1">
                {accidentData.map((item, index) => (
                  <div key={item.year} className="flex-1 flex flex-col items-center justify-end h-full">
                    <div className="flex gap-1 items-end h-full">
                      <div
                        className={`w-4 rounded-t bg-blue-500 transition-all duration-500 cursor-pointer hover:opacity-90 ${hoveredBarKey === `${index}-accidents` ? '' : ''}`}
                        style={{
                          ...touchSafeStyle,
                          height: `${animatedData[index] ? getBarHeight(item.accidents) : 0}px`,
                          transitionDelay: `${index * 45}ms`,
                          transform: hoveredBarKey === `${index}-accidents` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, 'accidents', index)}
                      />
                      <div
                        className={`w-4 rounded-t bg-red-500 transition-all duration-500 cursor-pointer hover:opacity-90 ${hoveredBarKey === `${index}-fatalities` ? '' : ''}`}
                        style={{
                          ...touchSafeStyle,
                          height: `${animatedData[index] ? getBarHeight(item.fatalities * FATALITIES_SCALE) : 0}px`,
                          transitionDelay: `${index * 45 + 20}ms`,
                          transform: hoveredBarKey === `${index}-fatalities` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, 'fatalities', index)}
                      />
                    </div>
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                  style={{
                    left: tooltipPos ? tooltipPos.x : -9999,
                    top: tooltipPos ? tooltipPos.y : -9999,
                    transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                  }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">
                    {tooltipData.label}: {tooltipData.value.toLocaleString('pt-BR')}
                    {tooltipData.label === 'Vítimas Fatais' && <span className="opacity-70"> (x{FATALITIES_SCALE})</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Legenda */}
          <div className="mt-6 flex justify-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-500 rounded"></div>
              <AlertTriangle className="w-4 h-4 mr-1" />
              <span>Acidentes</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-red-500 rounded"></div>
              <LifeBuoy className="w-4 h-4 mr-1" />
              <span>Vítimas Fatais (escala x{FATALITIES_SCALE})</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-green-50 to-teal-100">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-green-900 flex items-center gap-2">
            <TrendingDown className="w-6 h-6" />
            Redução dos Sinistros em 2024
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white/60 p-4 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">Queda Significativa nos Indicadores</h4>
            <p className="text-sm text-gray-700">
              Em 2024, foram registrados 848 acidentes (-4,5% vs 2023). As vítimas fatais também caíram 11%, para 217.
              Os dados indicam melhora na segurança da navegação, refletindo esforços de fiscalização e ordenamento.
            </p>
          </div>
          <div className="bg-white/60 p-4 rounded-lg">
            <h4 className="font-semibold text-green-800 mb-2">Tendência Histórica de Controle</h4>
            <p className="text-sm text-gray-700">
              Apesar de picos em 2015 e 2019, a série 2010–2024 apresenta leve queda, com taxas médias anuais de -0,9% (acidentes)
              e -1,3% (vítimas), sob fiscalização da Marinha do Brasil.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
